import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSendLetter, useCostEstimate } from '../hooks/useOrders'
import { useAddresses, type Address } from '@/client/modules/addresses/hooks/useAddresses'
import { CheckCircle, Loader2, ExternalLink, BookOpen } from 'lucide-react'
import { Link } from 'react-router-dom'

type AddressFields = {
  name: string
  addressLine1: string
  addressLine2: string
  suburb: string
  state: string
  postcode: string
}

const emptyAddress: AddressFields = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  suburb: '',
  state: '',
  postcode: '',
}

interface LibraryTemplate {
  id: string
  name: string
  description: string | null
  vertical: string
  merge_fields: Array<{ name: string; label: string; type: string; required: boolean }>
  content_preview: string
}

export function SendLetterPage() {
  const [recipient, setRecipient] = useState<AddressFields>({ ...emptyAddress })
  const [sender, setSender] = useState<AddressFields>({ ...emptyAddress })
  const [content, setContent] = useState('')
  const [colour, setColour] = useState(false)
  const [priority, setPriority] = useState(false)
  const [registeredPost, setRegisteredPost] = useState(false)
  const [contentMode, setContentMode] = useState<'write' | 'template'>('write')
  const [selectedTemplate, setSelectedTemplate] = useState<LibraryTemplate | null>(null)
  const [mergeData, setMergeData] = useState<Record<string, string>>({})
  const [templates, setTemplates] = useState<LibraryTemplate[]>([])

  const sendLetter = useSendLetter()
  const costEstimate = useCostEstimate()
  const { data: addressData } = useAddresses()

  const addresses = addressData?.addresses ?? []
  const senderAddresses = addresses.filter(a => a.type === 'sender' || a.type === 'both')
  const recipientAddresses = addresses.filter(a => a.type === 'recipient' || a.type === 'both')

  // Load template library
  useEffect(() => {
    fetch('/api/v1/templates/library')
      .then(r => r.json() as Promise<{ templates: LibraryTemplate[] }>)
      .then((data) => setTemplates(data.templates))
      .catch(() => {})
  }, [])

  // Pre-fill defaults from address book
  useEffect(() => {
    const defaultSender = addresses.find(a => a.is_default_sender)
    if (defaultSender && !sender.name) {
      populateAddress(defaultSender, setSender)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses])

  // Re-estimate cost when options change
  useEffect(() => {
    costEstimate.mutate({
      vertical: 'letters',
      options: { colour, priority, registered_post: registeredPost },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colour, priority, registeredPost])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // If using template, construct content from merge data via the API
    if (contentMode === 'template' && selectedTemplate) {
      fetch(`/api/v1/templates/${selectedTemplate.id}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merge_data: mergeData }),
      })
        .then(r => r.json() as Promise<{ html: string }>)
        .then((preview) => {
          sendLetter.mutate({
            to: recipient,
            from: sender,
            content: preview.html,
            colour,
            priority,
            registered_post: registeredPost,
          })
        })
        .catch((err) => {
          console.error('Template render failed:', err)
        })
      return
    }

    sendLetter.mutate({
      to: recipient,
      from: sender,
      content,
      colour,
      priority,
      registered_post: registeredPost,
    })
  }

  if (sendLetter.isSuccess) {
    const result = sendLetter.data
    return (
      <div className="container max-w-4xl py-8">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <h2 className="text-2xl font-bold">Letter Submitted</h2>
            <p className="text-muted-foreground">
              Order: <span className="font-mono font-medium text-foreground">{result.order_id}</span>
            </p>
            <p className="text-muted-foreground">
              Status: <span className="font-medium text-foreground">{result.status}</span>
            </p>
            <p className="text-lg font-mono font-bold">{result.cost}</p>

            {result.payment_url && (
              <Button asChild size="lg">
                <a href={result.payment_url} target="_blank" rel="noopener noreferrer">
                  Pay Now <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            )}

            {result.payment_method === 'credits' && (
              <p className="text-sm text-muted-foreground">
                Charged to credits. Balance after: {result.balance_after}
              </p>
            )}

            <div className="flex gap-3 mt-4">
              <Button asChild variant="outline">
                <Link to={`/dashboard/orders/${result.order_id}`}>View Order</Link>
              </Button>
              <Button onClick={() => {
                sendLetter.reset()
                setRecipient({ ...emptyAddress })
                setSender({ ...emptyAddress })
                setContent('')
                setColour(false)
                setPriority(false)
                setRegisteredPost(false)
                setSelectedTemplate(null)
                setMergeData({})
              }}>
                Send Another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Send a Letter</h1>
        <p className="text-muted-foreground">
          Fill in the details below and we will print and post your letter.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Recipient Address */}
        <Card>
          <CardHeader>
            <CardTitle>Recipient</CardTitle>
            <CardDescription>Who is this letter going to?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <AddressPicker
              addresses={recipientAddresses}
              onSelect={(addr) => populateAddress(addr, setRecipient)}
              label="Load from address book"
            />
            <AddressForm value={recipient} onChange={setRecipient} prefix="recipient" />
          </CardContent>
        </Card>

        {/* Sender Address */}
        <Card>
          <CardHeader>
            <CardTitle>Sender (Return Address)</CardTitle>
            <CardDescription>Your return address for the envelope.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <AddressPicker
              addresses={senderAddresses}
              onSelect={(addr) => populateAddress(addr, setSender)}
              label="Load from address book"
            />
            <AddressForm value={sender} onChange={setSender} prefix="sender" />
          </CardContent>
        </Card>

        {/* Letter Content */}
        <Card>
          <CardHeader>
            <CardTitle>Letter Content</CardTitle>
            <CardDescription>Type your letter or use a template.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={contentMode} onValueChange={(v) => setContentMode(v as 'write' | 'template')}>
              <TabsList className="mb-4">
                <TabsTrigger value="write">Write</TabsTrigger>
                <TabsTrigger value="template">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Use Template
                </TabsTrigger>
              </TabsList>

              <TabsContent value="write">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Dear..."
                  rows={16}
                  className="font-mono"
                  required={contentMode === 'write'}
                />
              </TabsContent>

              <TabsContent value="template">
                <TemplatePicker
                  templates={templates}
                  selected={selectedTemplate}
                  onSelect={(t) => {
                    setSelectedTemplate(t)
                    setMergeData({})
                  }}
                  mergeData={mergeData}
                  onMergeDataChange={setMergeData}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Options */}
        <Card>
          <CardHeader>
            <CardTitle>Printing & Postage Options</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <OptionRow
              id="colour"
              label="Colour printing"
              description="Print in full colour instead of black and white (+$1.00)"
              checked={colour}
              onChange={setColour}
            />
            <OptionRow
              id="priority"
              label="Priority post"
              description="Faster delivery via Australia Post Priority (+$2.00)"
              checked={priority}
              onChange={setPriority}
            />
            <OptionRow
              id="registered"
              label="Registered post"
              description="Delivery confirmation and signature on receipt (+$5.50)"
              checked={registeredPost}
              onChange={setRegisteredPost}
            />
          </CardContent>
        </Card>

        {/* Cost Estimate & Submit */}
        <Card>
          <CardContent className="flex items-center justify-between pt-6">
            <div>
              <p className="text-sm text-muted-foreground">Estimated cost</p>
              <p className="text-2xl font-bold font-mono">
                {costEstimate.data?.cost ?? '$3.50'}
              </p>
            </div>
            <Button type="submit" size="lg" disabled={sendLetter.isPending}>
              {sendLetter.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Letter
            </Button>
          </CardContent>
        </Card>

        {sendLetter.isError && (
          <p className="text-sm text-destructive">{sendLetter.error.message}</p>
        )}
      </form>
    </div>
  )
}

function populateAddress(addr: Address, setter: (v: AddressFields) => void) {
  setter({
    name: addr.name,
    addressLine1: addr.address_line1,
    addressLine2: addr.address_line2 ?? '',
    suburb: addr.suburb,
    state: addr.state,
    postcode: addr.postcode,
  })
}

function AddressPicker({
  addresses,
  onSelect,
  label,
}: {
  addresses: Address[]
  onSelect: (addr: Address) => void
  label: string
}) {
  if (addresses.length === 0) return null

  return (
    <Select onValueChange={(id) => {
      const addr = addresses.find(a => a.id === id)
      if (addr) onSelect(addr)
    }}>
      <SelectTrigger>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        {addresses.map((addr) => (
          <SelectItem key={addr.id} value={addr.id}>
            {addr.label} — {addr.name}, {addr.suburb} {addr.state}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function TemplatePicker({
  templates,
  selected,
  onSelect,
  mergeData,
  onMergeDataChange,
}: {
  templates: LibraryTemplate[]
  selected: LibraryTemplate | null
  onSelect: (t: LibraryTemplate) => void
  mergeData: Record<string, string>
  onMergeDataChange: (data: Record<string, string>) => void
}) {
  if (templates.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No templates available yet.</p>
  }

  // Strip HTML tags for safe display of content_preview
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

  return (
    <div className="space-y-4">
      <Select
        value={selected?.id ?? ''}
        onValueChange={(id) => {
          const t = templates.find(t => t.id === id)
          if (t) onSelect(t)
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Choose a template..." />
        </SelectTrigger>
        <SelectContent>
          {templates.map((t) => (
            <SelectItem key={t.id} value={t.id}>
              {t.name} {t.description ? `— ${t.description}` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selected && selected.merge_fields.length > 0 && (
        <div className="space-y-3 rounded-lg border p-4">
          <p className="text-sm font-medium">Fill in the template fields:</p>
          {selected.merge_fields.map((field) => (
            <div key={field.name}>
              <Label htmlFor={`merge-${field.name}`}>
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              <Input
                id={`merge-${field.name}`}
                type={field.type === 'date' ? 'date' : 'text'}
                value={mergeData[field.name] ?? ''}
                onChange={(e) => onMergeDataChange({ ...mergeData, [field.name]: e.target.value })}
                required={field.required}
              />
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="rounded-lg bg-muted/50 p-4">
          <p className="text-xs text-muted-foreground mb-2">Preview:</p>
          <p className="text-sm">
            {(() => {
              let preview = selected.content_preview
              for (const field of selected.merge_fields) {
                preview = preview.replace(
                  new RegExp(`\\{\\{${field.name}\\}\\}`, 'g'),
                  `[${field.label}]`
                )
              }
              return stripHtml(preview)
            })()}
          </p>
        </div>
      )}
    </div>
  )
}

function OptionRow({ id, label, description, checked, onChange }: {
  id: string
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <Label htmlFor={id}>{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

function AddressForm({
  value,
  onChange,
  prefix,
}: {
  value: AddressFields
  onChange: (v: AddressFields) => void
  prefix: string
}) {
  const update = (field: keyof AddressFields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [field]: e.target.value })

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor={`${prefix}-name`}>Full name</Label>
        <Input id={`${prefix}-name`} value={value.name} onChange={update('name')} required />
      </div>
      <div>
        <Label htmlFor={`${prefix}-address1`}>Address line 1</Label>
        <Input id={`${prefix}-address1`} value={value.addressLine1} onChange={update('addressLine1')} required />
      </div>
      <div>
        <Label htmlFor={`${prefix}-address2`}>Address line 2</Label>
        <Input id={`${prefix}-address2`} value={value.addressLine2} onChange={update('addressLine2')} />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor={`${prefix}-suburb`}>Suburb</Label>
          <Input id={`${prefix}-suburb`} value={value.suburb} onChange={update('suburb')} required />
        </div>
        <div>
          <Label htmlFor={`${prefix}-state`}>State</Label>
          <Input id={`${prefix}-state`} value={value.state} onChange={update('state')} placeholder="NSW" required />
        </div>
        <div>
          <Label htmlFor={`${prefix}-postcode`}>Postcode</Label>
          <Input id={`${prefix}-postcode`} value={value.postcode} onChange={update('postcode')} placeholder="2000" required />
        </div>
      </div>
    </div>
  )
}
